import type { Id, ToastOptions } from "react-toastify";
import { toast } from "react-toastify";
import type { ToastAction, ToastVariant } from "./customToast";
import AppToast from "./customToast";

const baseConfig: ToastOptions = {
  closeButton: false,
  icon: false,
  hideProgressBar: true,
};

function showToast(variant: ToastVariant, title: string, message: string) {
  toast(
    <AppToast title={title} message={message} variant={variant} />,
    baseConfig
  );
}

export interface ConfirmToastOptions {
  title?: string;
  message: string;
  variant?: ToastVariant;
  actions: ToastAction[];
  /** Shows a "<label> in Ns" counter beside the actions. Display only. */
  countdownMs?: number;
  countdownLabel?: string;
  /** Fired when the user dismisses the toast with the ✕ instead of choosing. */
  onDismiss?: () => void;
}

export const toaster = {
  success: (message: string, title?: string) => {
    showToast("success", title || "Success", message);
  },
  error: (message: string, title?: string) => {
    showToast("error", title || "Error", message);
  },

  warn: (message: string, title?: string) => {
    showToast("warning", title || "Warning", message);
  },

  info: (message: string, title?: string) => {
    showToast("info", title || "Info", message);
  },

  /**
   * A toast the user has to answer. It never auto-closes on its own — picking
   * an action closes it, and the returned function closes it from the outside
   * (e.g. when the decision has already been made elsewhere).
   */
  confirm: ({
    title,
    message,
    variant = "warning",
    actions,
    countdownMs,
    countdownLabel,
    onDismiss,
  }: ConfirmToastOptions): (() => void) => {
    // The toast id only exists after `toast()` returns, but the buttons that
    // need it are built before — hence the holder.
    const holder: { id?: Id } = {};
    const close = () => {
      if (holder.id !== undefined) toast.dismiss(holder.id);
    };

    holder.id = toast(
      <AppToast
        title={title || "Confirm"}
        message={message}
        variant={variant}
        countdownMs={countdownMs}
        countdownLabel={countdownLabel}
        onClose={() => {
          close();
          onDismiss?.();
        }}
        actions={actions.map((action) => ({
          ...action,
          onClick: () => {
            close();
            action.onClick();
          },
        }))}
      />,
      {
        ...baseConfig,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      }
    );

    return close;
  },
};
